import { Q } from "@nozbe/watermelondb";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import { useFlashMessages } from "@towersystems/roam-common-native";
import {
  LayoutNode as LayoutNodeEntity,
  LayoutNodeInput,
  UpdateLayoutNodeInput,
} from "@towersystems/roam-common/lib/generated-types";
import { useRef } from "react";
import { useLayoutProvider } from "../../../modules/point-of-sale/utils/layout";
import { useActiveLayout } from "../../app-state/hooks";
import { DragDropActionTypes } from "../../drag-drop";
import {
  deleteLayoutKeyDefinition,
  shiftLayoutKeysDefinition,
  swapLayoutKeysDefinition,
  updateLayoutKeyDefinition,
} from "../definitions";
import { applyDefaultLayoutDefinition } from "../definitions/apply-default-layout-definition";
import { createLayoutKeyDefinition } from "../definitions/create-layout-key-definition";
import { InvalidLayoutActionError, RemoteError } from "../errors";
import { LayoutAction } from "../types";
import { attemptLocalAction, attemptRemoteAction } from "../utils";
import { updateLayoutNodePositionDefinition } from "../definitions/update-layout-nodes-position-definition";
import LayoutNode from "../../data/model/LayoutNode";

type ActionState = {
  action: LayoutAction;
  state: any;
};

const MAX_ACTION_HISTORY = 3;

/**
 * Controller hook that tracks layout edit state while performing local/remote actions
 */
export function useLayoutEditController() {
  const { layoutNodes } = useLayoutProvider();
  const { showMessage } = useFlashMessages();
  const actionHistory = useRef<ActionState[]>([]).current;

  const createLayoutKey = useCreateLayoutKey();
  const updateLayoutKey = useUpdateLayoutKey();
  const deleteLayoutKey = useDeleteLayoutKey();
  const swapLayoutKeys = useSwapLayoutKeys();
  const shiftLayoutKeys = useShiftLayoutKeys();
  const applyDefaultLayoutConfiguration = useApplyDefaultLayoutConfiguration();
  const restoreLayoutState = useRestoreLayoutState();
  const updateLayoutNodePositions = useUpdateLayoutNodePositions();

  async function undoLayoutChange(errorMessage: string) {
    const undoAction = actionHistory.pop();

    if (!undoAction?.state) return;

    const { state } = undoAction;

    return restoreLayoutState(state)
      .then(() => {
        showMessage({
          message: "Layout changes could not complete",
          severity: "warning",
        });
      })
      .catch((e) => {
        showMessage({
          message: errorMessage,
          severity: "error",
        });
      });
  }

  async function appendActionHistory(action: LayoutAction) {
    if (actionHistory.length >= MAX_ACTION_HISTORY) {
      actionHistory.shift();
    }
    actionHistory.push({
      action,
      state: layoutNodes.map(toNodeProperties),
    });
  }

  async function performLayoutAction(action: LayoutAction) {
    await appendActionHistory(action);
    try {
      switch (action.type) {
        case DragDropActionTypes.CREATE_POS_LAYOUT_KEY: {
          await createLayoutKey(action.payload);
          break;
        }
        case DragDropActionTypes.UPDATE_POS_LAYOUT_KEY: {
          await updateLayoutKey(action.payload.id, action.payload as any);
          break;
        }
        case DragDropActionTypes.DELETE_POS_LAYOUT_KEY: {
          const { id } = action.payload as any;
          await deleteLayoutKey(id);
          break;
        }
        case DragDropActionTypes.SWAP_POS_LAYOUT_KEYS: {
          const { firstNodeId, secondNodeId } = action.payload as any;
          await swapLayoutKeys(firstNodeId, secondNodeId);
          break;
        }
        case DragDropActionTypes.SHIFT_POS_LAYOUT_KEYS: {
          const { id, position } = action.payload as any;
          await shiftLayoutKeys(id, position);
          break;
        }
        case DragDropActionTypes.APPLY_DEFAULT_LAYOUT_CONFIGURATION: {
          const { id } = action.payload as any;
          await applyDefaultLayoutConfiguration(id);
          break;
        }
        case DragDropActionTypes.UPDATE_LAYOUT_NODE_POSITIONS: {
          const { layoutId, nodes } = action.payload as any;
          await updateLayoutNodePositions(layoutId, nodes);
          break;
        }

        default:
          throw new InvalidLayoutActionError();
      }
    } catch (error: any) {
      if (error instanceof InvalidLayoutActionError) {
        return;
      } else if (error instanceof RemoteError) {
        await undoLayoutChange(error.message);
      }
    }
  }

  return performLayoutAction;
}

// individual action hooks

function useRestoreLayoutState() {
  const database = useDatabase();
  const { layout } = useActiveLayout();

  return async (state: SerializedLayoutNode[]) => {
    if (!layout) return;

    await database.write(async () => {
      const layoutNodes = await database.collections
        .get<LayoutNode>("layout_nodes")
        .query(Q.where("layout_id", layout.id))
        .fetch();

      const deletionBatch = layoutNodes.map((node) =>
        node.prepareDestroyPermanently()
      );

      await database.batch(...deletionBatch);
    });

    const creationBatch = state.map((node) => {
      return database.collections
        .get<LayoutNode>("layout_nodes")
        .prepareCreate((n) => {
          n._raw.id = node._raw.id;
          n.layout.id = node.layoutId || null;
          n.label = node.label || null;
          n.color = node.color;
          n.showImage = node.showImage || false;
          n.productId = node.productId || null;
          n.type = node.type || null;
          n.position = node.position || 0;
          n.action = node.action || null;
          n.link = node.link || null;
          n.filter = node.filter || null;
        });
    });

    await database.write(async () => {
      await database.batch(creationBatch);
    });
  };
}

function useCreateLayoutKey() {
  const database = useDatabase();

  return async (input: LayoutNodeInput) => {
    const remoteResult = await attemptRemoteAction(() =>
      createLayoutKeyDefinition.config.remoteAction(input)
    );

    const localResult = await attemptLocalAction(() =>
      createLayoutKeyDefinition.config.localAction(database, {
        ...input,
        id: remoteResult.id,
      })
    );

    return remoteResult;
  };
}

function useUpdateLayoutKey() {
  const database = useDatabase();

  return async (id: string, input: LayoutNodeInput) => {
    const localResult = await attemptLocalAction(() =>
      updateLayoutKeyDefinition.config.localAction(database, id, {
        ...input,
        id,
      })
    );

    const remoteResult = await attemptRemoteAction(() =>
      updateLayoutKeyDefinition.config.remoteAction({ ...input, id })
    );

    return remoteResult;
  };
}

function useSwapLayoutKeys() {
  const database = useDatabase();

  return async (originKeyId: string, targetKeyId: string) => {
    const input = {
      firstNodeId: originKeyId,
      secondNodeId: targetKeyId,
    };

    const localResult = await attemptLocalAction(() =>
      swapLayoutKeysDefinition.config.localAction(database, input)
    );

    const remoteResult = await attemptRemoteAction(() =>
      swapLayoutKeysDefinition.config.remoteAction(input)
    );

    return remoteResult;
  };
}

function useUpdateLayoutNodePositions() {
  const database = useDatabase();
  return async (layoutId: string, nodes: UpdateLayoutNodeInput[]) => {
    await attemptLocalAction(() =>
      updateLayoutNodePositionDefinition.config.localAction(
        database,
        layoutId,
        nodes
      )
    );

    const remoteResult = await attemptRemoteAction(() =>
      updateLayoutNodePositionDefinition.config.remoteAction(layoutId, nodes)
    );

    return remoteResult;
  };
}

function useShiftLayoutKeys() {
  const database = useDatabase();

  return async (id: string, position: number) => {
    const input = {
      id,
      position,
    };

    const localResult = await attemptLocalAction(() =>
      shiftLayoutKeysDefinition.config.localAction(database, input)
    );

    const remoteResult = await attemptRemoteAction(() =>
      shiftLayoutKeysDefinition.config.remoteAction(input)
    );

    return remoteResult;
  };
}

function useDeleteLayoutKey() {
  const database = useDatabase();

  return async (id: string) => {
    const localResult = await attemptLocalAction(() =>
      deleteLayoutKeyDefinition.config.localAction(database, id)
    );

    const remoteResult = await attemptRemoteAction(() =>
      deleteLayoutKeyDefinition.config.remoteAction(id)
    );

    return remoteResult;
  };
}

export function useApplyDefaultLayoutConfiguration() {
  const database = useDatabase();

  return async (id: string) => {
    const remoteResult = await attemptRemoteAction(() =>
      applyDefaultLayoutDefinition.config.remoteAction(id)
    );

    const nodesToCreate = remoteResult.nodes;

    const localResult = await attemptLocalAction(() =>
      applyDefaultLayoutDefinition.config.localAction(
        database,
        remoteResult.id,
        nodesToCreate
      )
    );
    return remoteResult;
  };
}

export interface SerializedLayoutNode {
  _raw: { id: string };
  layoutId: string;
  label: string | null;
  color: string | null;
  showImage: boolean;
  productId: string | null;
  type: string | null;
  position: number;
  action: any | null;
  link: any | null;
  filter: any | null;
}

function toNodeProperties(node: LayoutNode): SerializedLayoutNode {
  const {
    layout: { id: layoutId },
    label,
    color,
    showImage,
    productId,
    type,
    position,
    action,
    link,
    filter,
  } = node;

  return {
    _raw: { id: node._raw.id },
    layoutId,
    label,
    color,
    showImage,
    productId,
    type,
    position,
    action,
    link,
    filter,
  };
}
